import { UserType } from "app/api/user/user.types";
import axios, { RawAxiosRequestHeaders } from "axios";
import { API_BASEURL } from "../api";
import { ProductObject } from "./product.type";
import { categoryList } from "app/atoms/category.atom";
import { getDefaultStore } from "jotai";
import { categoryI } from "app/api/category/category.types";
import { toKebabCase } from "app/utils/fn";

// Helper function to validate category and subcategory
const validateCategories = (
  category?: string,
  subCategory?: string,
  categories?: categoryI[],
): {
  category?: { name: string; id: string };
  subcategory?: { name: string; id: string };
} => {
  const result: {
    category?: { name: string; id: string };
    subcategory?: { name: string; id: string };
  } = {};

  if (!category && !subCategory) return result; // No validation needed if no category/subcategory provided

  let categoriesToValidate: categoryI[] = [];

  // Use provided categories if available, otherwise try to get from store
  if (categories && categories.length > 0) {
    categoriesToValidate = categories;
  } else {
    const store = getDefaultStore();
    categoriesToValidate = store.get(categoryList) as categoryI[];
  }

  // Skip validation if no categories available
  if (!categoriesToValidate || categoriesToValidate.length === 0) {
    return result;
  }

  // Find and validate category
  if (categoriesToValidate && category) {
    const foundCategory = categoriesToValidate.find((cat) => {
      return cat._id === category || toKebabCase(cat.name) === toKebabCase(category);
    });

    if (!foundCategory) {
      throw new axios.AxiosError(`page '${category}' not found`, "404");
    }

    result.category = {
      name: foundCategory.name,
      id: foundCategory._id,
    };

    // Find and validate subcategory
    if (subCategory && foundCategory.subCategory) {
      const foundSubCategory = foundCategory.subCategory.find(
        (sub) =>
          sub._id === subCategory ||
          sub.name === subCategory ||
          toKebabCase(sub.name) === toKebabCase(subCategory),
      );

      if (!foundSubCategory) {
        throw new axios.AxiosError(
          `Subcategory '${subCategory}' not found in category '${category}'`,
          "404",
        );
      }

      result.subcategory = {
        name: foundSubCategory.name,
        id: foundSubCategory._id,
      };
    } else if (subCategory && !foundCategory.subCategory) {
      throw new axios.AxiosError(`Category '${category}' has no subcategories`, "404");
    }
  }

  return result;
};

export type FilterParams = {
  page?: number;
  limit?: number;
  status?: string;
  userId?: string;
  productId?: string;
};

export type ProductFilter = {
  category?: string;
  subCategory?: string;
  search?: string;
  supplier?: string;
  filters?: Array<string>;
  colours?: Array<string>;
  labels?: Array<string>;
  sort?: string;
  minPrice?: string;
  maxPrice?: string;
  minQty?: string;
  maxQty?: string;
  userType?: string;
  normalSearch?: string;
  /** Typesense natural language search (browser path); forwarded as nl_query / nl_model_id when applicable. */
  nlQuery?: boolean | string;
  nlModelId?: string;
  categories?: categoryI[]; // For server-side validation
} & FilterParams;

export const fetchProductsApi = async (params: ProductFilter) => {
  const axiosParams: any = {};

  if (params.userType) axiosParams.role = params.userType;
  if (params.page) axiosParams.page = params.page;
  if (params.limit) axiosParams.limit = params.limit;
  if (params.sort) axiosParams.sort = params.sort;
  if (params.category) axiosParams.category = params.category;
  if (params.subCategory) axiosParams.subCategory = params.subCategory;
  if (params.search) axiosParams.search = params.search;
  if (params.filters) axiosParams.filter = params.filters;
  if (params.colours) axiosParams.colours = params.colours;
  if (params.maxPrice) axiosParams.maxPrice = params.maxPrice;
  if (params.minPrice) axiosParams.minPrice = params.minPrice;

  const res = await axios.get(`${API_BASEURL}/products`, { params: axiosParams });
  return res;
};

export const fetchProductsTextSearchApi = async (
  params: ProductFilter,
): Promise<{
  products: ProductObject[];
  pagination: {
    hasNextPage: boolean;
    hasPrevPage: boolean;
    limit: number;
    nextPage: number | null;
    page: number;
    prevPage: number | null;
    total: number;
    totalPages: number;
  };
}> => {
  const axiosParams: any = {
    isAdmin: false,
  };

  if (params.userType) axiosParams.role = params.userType;
  if (params.page) axiosParams.page = params.page;
  if (params.limit) axiosParams.limit = params.limit;
  if (params.sort) axiosParams.sort = params.sort;
  if (params.minQty) axiosParams.minQty = params.minQty;
  if (params.maxQty) axiosParams.maxQty = params.maxQty;
  if (params.minPrice) axiosParams.minPrice = params.minPrice;
  if (params.maxPrice) axiosParams.maxPrice = params.maxPrice;
  if (params.category) axiosParams.category = params.category;
  if (params.subCategory) axiosParams.subCategory = params.subCategory;
  if (params.search) axiosParams.search = params.search;
  if (params.normalSearch) axiosParams.normalSearch = params.normalSearch;
  if (params.supplier) axiosParams.suppliers = params.supplier;
  if (params.filters) axiosParams.filter = params.filters;
  if (params.colours) axiosParams.colours = params.colours.map((colour) => colour.toLowerCase());
  if (params.labels) axiosParams.labels = params.labels.map((label) => label.toLowerCase());

  const { data } = await axios.get(`${API_BASEURL}/products/product-search`, {
    params: axiosParams,
  });
  return data;
};

export const fetchProductShowCase = async (params: { userType?: string; cursor?: string }) => {
  const axiosParams: any = {};

  if (params.userType) axiosParams.userType = params.userType;
  if (params.cursor) axiosParams.cursor = params.cursor;

  const res = await axios.get(`${API_BASEURL}/home-page-component`, { params: axiosParams });
  return res;
};

export const fetchHomePageInitialData = async (params?: {
  userType?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const axiosParams: any = {
      page: params?.page || 1,
      limit: params?.limit || 12,
    };

    if (params?.userType) axiosParams.userType = params.userType;

    const { data } = await axios.get(`${API_BASEURL}/home-page-component/initial-data`, {
      params: axiosParams,
    });
    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

export const fetchLatestProduct = async (params?: ProductFilter): Promise<ProductObject[]> => {
  const axiosParams: any = {
    limit: params?.limit || 8,
    page: params?.page || 1,
  };

  if (params?.userType) axiosParams.userType = params.userType;

  const response = await axios.get(`${API_BASEURL}/products/latest`, { params: axiosParams });
  return response.data;
};
//

export const fetchProductStockLevelApi = async (productId: string) => {
  const response = await axios.get(`${API_BASEURL}/products/check-stock-levels/${productId}`);
  return response.data;
};

export const fetchTopSellingProductsApi = async (params?: { page: number; limit: number }) => {
  const axiosParams = {
    page: params?.page || 1,
    limit: params?.limit || 8,
  };

  const { data } = await axios.get(`${API_BASEURL}/products/top-selling`, { params: axiosParams });
  return data;
};

export const fetchTrendingProductsApi = async (params?: {
  userType?: UserType;
  page?: number;
  limit?: number;
}) => {
  const axiosParams: any = {
    page: params?.page || 1,
    limit: params?.limit || 8,
  };

  if (params?.userType) axiosParams.userType = params.userType;

  const { data } = await axios.get(`${API_BASEURL}/products/trending`, { params: axiosParams });
  return data;
};

export const fetchRelatedProductsApi = async (
  userType: string,
  categoryId: string,
  subCategoryId: string,
  params?: { page: number; limit: number },
) => {
  const axiosParams = {
    page: params?.page || 1,
    limit: params?.limit || 8,
    userType: userType,
  };

  const { data } = await axios.get(
    `${API_BASEURL}/products/related/${categoryId}/${subCategoryId}`,
    { params: axiosParams },
  );
  return data;
};

export const fetchHotProductsApi = async (params?: {
  userType?: string;
  page: number;
  limit: number;
}) => {
  try {
    const axiosParams: any = {
      page: params?.page,
      limit: params?.limit || 8,
    };

    if (params?.userType) axiosParams.userType = params.userType;

    const { data } = await axios.get(`${API_BASEURL}/products/hot-products`, {
      params: axiosParams,
    });
    return data;
  } catch (err) {
    console.error(err);
  }
};

export const getProductInfo = async (slug: string) => {
  const { data } = await axios.get(`${API_BASEURL}/products/${slug}`);
  return data;
};

export const fetchProductCategories = async () => {
  const res = await axios.get(`${API_BASEURL}/product-category`);

  return res.data;
};

export const fetchProductByCategory = async (category: string) => {
  const res = await axios.get(`${API_BASEURL}/products/categories/${category}`, {
    params: { limit: 6 },
  });

  return res;
};

export const fetchProductBySlug = async (slug: string): Promise<any> => {
  const { data } = await axios.get(`${API_BASEURL}/products/${slug}`);

  return { data };
};

export const fetchAllProductsApi = async (params: ProductFilter) => {
  // Validate categories if no search is provided but category/subcategory exist
  let validatedCategories:
    | {
        category?: { name: string; id: string };
        subcategory?: { name: string; id: string };
      }
    | undefined = undefined;

  if (!params.search && (params.category || params.subCategory)) {
    validatedCategories = validateCategories(
      params.category,
      params.subCategory,
      params.categories,
    );
  }

  const axiosParams: any = {};

  if (params.userType) axiosParams.userType = params.userType;
  if (params.page) axiosParams.page = params.page;
  if (params.limit) axiosParams.limit = params.limit;
  if (params.sort) axiosParams.sort = params.sort;

  // Use validated category/subcategory IDs if available, otherwise use kebab case
  if (validatedCategories?.category) {
    axiosParams.category = validatedCategories.category.id;
  } else if (params.category) {
    axiosParams.category = toKebabCase(params.category);
  }

  if (validatedCategories?.subcategory) {
    axiosParams.subCategory = validatedCategories.subcategory.id;
  } else if (params.subCategory) {
    axiosParams.subCategory = toKebabCase(params.subCategory);
  }

  if (params.search) axiosParams.search = params.search;
  if (params.supplier) axiosParams.suppliers = params.supplier;
  if (params.filters) axiosParams.filter = params.filters;
  if (params.colours) axiosParams.colours = params.colours.map((colour) => colour.toLowerCase());
  if (params.labels) axiosParams.labels = params.labels.map((label) => label.toLowerCase());

  // console.log({ API_BASEURL, axiosParams, validatedCategories });

  const res = await axios.get(`${API_BASEURL}/products`, { params: axiosParams });
  return res;
};

export const SearchAllProductsApi = async (params: ProductFilter) => {
  const axiosParams: any = {};
  if (params.userType) axiosParams.userType = params.userType;
  if (params.page) axiosParams.page = params.page;
  if (params.limit) axiosParams.limit = params.limit;
  if (params.sort) axiosParams.sort = params.sort;
  if (params.minQty) axiosParams.minQty = params.minQty;
  if (params.maxQty) axiosParams.maxQty = params.maxQty;
  if (params.minPrice) axiosParams.minPrice = params.minPrice;
  if (params.maxPrice) axiosParams.maxPrice = params.maxPrice;
  if (params.category) axiosParams.category = toKebabCase(params.category);
  if (params.subCategory) axiosParams.subCategory = toKebabCase(params.subCategory);
  if (params.search) axiosParams.search = params.search;
  if (params.supplier) axiosParams.suppliers = params.supplier;
  if (params.filters) axiosParams.filter = params.filters;
  if (params.colours) axiosParams.colours = params.colours.map((colour) => colour.toLowerCase());
  if (params.labels) axiosParams.labels = params.labels.map((label) => label.toLowerCase());
  if (params.normalSearch) axiosParams.normalSearch = params.normalSearch;
  if (params.nlQuery === true || params.nlQuery === "true") axiosParams.nlQuery = true;
  if (params.nlModelId) axiosParams.nlModelId = params.nlModelId;

  const res = await axios.get(`${API_BASEURL}/products/product-search`, { params: axiosParams });

  return res;
};

export const newProductReviewApi = async (
  payload: { comment: string; rating: number; productId: string },
  headers: RawAxiosRequestHeaders,
) => {
  const { productId, ...rest } = payload;
  const result = await axios.post(`${API_BASEURL}/comments/${productId}`, rest, {
    headers: { "Content-Type": "application/json", ...headers },
  });

  return result;
};

export const getAllProductComments = async (productId: string) => {
  const result = await axios.get(`${API_BASEURL}/comments/${productId}`);

  return result;
};

export const getProductRatingApi = async (productId: string) => {
  const result = await axios.get(`${API_BASEURL}/comments/${productId}/rating`);
  return result;
};

//

export const markProductAsViewedApi = async (
  productId: string,
  headers: RawAxiosRequestHeaders,
) => {
  const result = await axios.post(
    `${API_BASEURL}/product-views/${productId}`,
    {},
    {
      headers: { "Content-Type": "application/json", ...headers },
    },
  );

  return result;
};
